<template>
  <div ref="notesLayer" class="notes" :class="notes.length > 0 ? 'notes--hasItems' : ''">
    <slot name="header"></slot>
    <div
      v-for="(noteItem, index) in notesList"
      :key="noteItem.id"
      class="notes__list"
      :class="{
        'notes__list--last': index === notesList.length - 1
      }"
    >
      <NoteItem
        :note="noteItem"
        :can-edit="canEdit(noteItem)"
        :show-extra-info="props.showExtraNoteInfo"
        @fetch-notes="fetchNotes"
      />
    </div>
    <div ref="textAreaForm" class="notes__form">
      <div class="notes__textarea-wrapper" @click="setAddNoteActive">
        <FormTextArea
          v-model="note"
          :inert="!isAddNoteActive"
          :placeholder="textAreaPlaceholder"
          resize="none"
          rows="5"
        />
      </div>
      <wa-button
        v-if="props.hasSubmit"
        ref="saveNoteButton"
        :disabled="!isAddNoteActive"
        style="width: max-content"
        variant="brand"
        @click="handleCreateNote"
      >
        {{ $t('globals.confirm') }}</wa-button
      >
    </div>
    <wa-dialog :open="showWarning" label="Achtung" no-close @wa-request-close="handleClose">
      {{ $t('notes.leaveConfirmation') }}
      <wa-button
        slot="footer"
        variant="neutral"
        appearance="outlined"
        @click="((isAddNoteActive = false), (note = ''), (showWarning = false))"
        >{{ $t('globals.yes') }}</wa-button
      >
      <wa-button
        slot="footer"
        variant="brand"
        @click="((isAddNoteActive = true), (showWarning = false))"
        >{{ $t('globals.no') }}</wa-button
      >
    </wa-dialog>
  </div>
</template>

<script setup>
import FormTextArea from '@/components/Form/FormTextArea/FormTextArea.vue'
import NoteItem from '@/components/shared/Notes/NoteItem/NoteItem.vue'
import '@awesome.me/webawesome/dist/components/dialog/dialog.js'
import '@awesome.me/webawesome/dist/components/button/button.js'
import { onClickOutside } from '@vueuse/core'
import { computed, onMounted, ref } from 'vue'

const emit = defineEmits(['fetchNotes', 'createNote'])
const props = defineProps({
  notes: {
    type: Array,
    default: () => []
  },
  textAreaPlaceholder: {
    type: String,
    default: 'Add a note'
  },
  showExtraNoteInfo: {
    type: Boolean,
    default: false
  },
  disabledEditTypes: {
    type: Array,
    default() {
      return []
    }
  },
  hasSubmit: {
    type: Boolean,
    default: true
  }
})

const notesList = computed(() => props.notes)
const note = ref('')
const noteBackup = ref('')
const isAddNoteActive = ref(false)
const textAreaForm = ref()
const notesLayer = ref()
const showWarning = ref(false)

function fetchNotes() {
  emit('fetchNotes')
}

function handleCreateNote() {
  emit('createNote', { note: note.value })
  note.value = ''
  showWarning.value = false
}

function handleClose(event) {
  event.stopPropagation()
  if (['overlay', 'keyboard'].includes(event.detail.source)) {
    event.preventDefault()
  }
}

function setAddNoteActive() {
  noteBackup.value = note.value
  isAddNoteActive.value = true

  // focus on textarea
  setTimeout(() => {
    textAreaForm.value.querySelector('wa-textarea').focus()
  }, 100)

  setTimeout(() => {
    notesLayer.value.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
  }, 100)
}

onMounted(() => fetchNotes())
onClickOutside(textAreaForm, () => {
  noteBackup.value = note.value
  isAddNoteActive.value = false
  if (note.value?.length > 0 && !showWarning.value && props.hasSubmit) {
    showWarning.value = true
  }
})

function canEdit(note) {
  let type = note.attachable?.type

  if (note?.metadata?.prescription) {
    type = 'prescriptions'
  }

  return !props.disabledEditTypes.includes(type)
}

defineExpose({
  note
})
</script>

<style lang="postcss" src="./Notes.css" />
